import { getAaveStyleProtocolTokenMap, getAaveTypePoolAddress } from '../assets'
import { getAavesForChain } from '../utils'
import {
  RELEVANT_BALANCER_V2_FORKS,
  RELEVANT_BALANCER_V3_FORKS,
  RELEVANT_LENDING_ASSETS,
  RELEVANT_MORPHOB_FORKS,
  RELEVANT_UNISWAP_V4_FORKS,
} from './assets'
import { Call } from '../utils/multicall'
import { MulticallRetryFunction, TokenList } from '../types'
import { FlashAbi } from '../abis/flashloan-united/flashAbi'
import { FlashLiquiditiesOnChain, FlashLoanIds } from './types'
import { FLASH_LOAN_IDS, isFlashLoanSourceExcluded } from '@1delta/dex-registry'
import { formatUnits, zeroAddress } from 'viem'
import { AAVE_V2_LENDERS, AAVE_V3_LENDERS } from '@1delta/lender-registry'
import _ from 'lodash'
import { MULTICALL_ADDRESS } from '../utils/constants'

const DEFAULT_BATCH_SIZE = 4096

const isValidResult = (v: any): v is bigint => typeof v === 'bigint'

/** Native (zero-address) legs are always 18-decimal on every EVM chain we serve. */
const NATIVE_DECIMALS = 18

/**
 * `decimals()` comes back as a `uint8` (a JS `number` under viem) but a few
 * non-standard tokens declare it `uint256` (a `bigint`). Accept both, reject
 * anything outside the plausible ERC-20 range.
 */
function parseDecimalsResult(v: any): number | undefined {
  const n = typeof v === 'bigint' ? Number(v) : v
  if (typeof n !== 'number' || !Number.isInteger(n) || n < 0 || n > 36) {
    return undefined
  }
  return n
}

enum BalanceFetchCall {
  balanceOf = 'balanceOf',
  getEthBalance = 'getEthBalance',
}

// Aave V3 flash loan flag enabled flag
const FLASHLOAN_ENABLED_MASK = BigInt(
  '0xFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFFF7FFFFFFFFFFFFFFF',
)
function getFlashLoanEnabled(data: bigint): boolean {
  return (data & ~FLASHLOAN_ENABLED_MASK) !== BigInt(0)
}

export async function fetchFlashLiquidityForChain(
  chain: string,
  multicallRetry: MulticallRetryFunction,
  list: TokenList = {},
) {
  let callLengths: { [l: string]: number } = {}
  let aaveAssets: { [l: string]: string[] } = {}

  let aaveCalls: Call[] = []

  const aaves = getAavesForChain()

  const aaveProtocols = (aaves[chain] ?? []).filter(
    (l) => !isFlashLoanSourceExcluded(chain, l),
  )

  // 1) Build aave calls and collect all aave underlyings
  const allAaveUnderlyings: string[] = []

  aaveProtocols.forEach((aaveFork) => {
    const underlyingsAndATokens = Object.entries(
      getAaveStyleProtocolTokenMap(chain, aaveFork),
    )

    const pool = getAaveTypePoolAddress(chain, aaveFork)

    const tokenCalls = underlyingsAndATokens.flatMap(
      ([a, b]: [string, any]) => [
        { name: BalanceFetchCall.balanceOf, address: a, params: [b.aToken] },
        { name: 'getConfiguration', address: pool, params: [a] },
      ],
    )

    const underlyings = underlyingsAndATokens.map((a) => a[0])
    aaveAssets[aaveFork] = underlyings
    allAaveUnderlyings.push(...underlyings)

    callLengths[aaveFork] = tokenCalls.length * 2 + 1

    aaveCalls.push(...tokenCalls, {
      name: 'FLASHLOAN_PREMIUM_TOTAL',
      address: pool,
    })
  })

  // 2) Get non-aave assets (compound V2/V3 reserves)
  // 3) Merge with aave underlyings into a unified distinct list
  const lendingAssets = RELEVANT_LENDING_ASSETS()?.[chain] ?? []
  const unifiedAssets = _.uniq([...allAaveUnderlyings, ...lendingAssets])

  // @ts-ignore
  const morphoBs = RELEVANT_MORPHOB_FORKS()?.[chain] ?? []

  const buildBalanceCalls = (
    forks: { pool: string; address: string }[],
  ): Call[] => {
    const result: Call[] = []
    for (const fork of forks) {
      callLengths[fork.pool] = unifiedAssets.length
      for (const address of unifiedAssets) {
        result.push(
          address === zeroAddress
            ? {
                name: BalanceFetchCall.getEthBalance,
                address: MULTICALL_ADDRESS[chain],
                params: [fork.address],
              }
            : {
                name: BalanceFetchCall.balanceOf,
                address,
                params: [fork.address],
              },
        )
      }
    }
    return result
  }

  const morphoCalls = buildBalanceCalls(morphoBs)

  const balancerV2s = RELEVANT_BALANCER_V2_FORKS[chain] ?? []
  const balancerV2Calls = buildBalanceCalls(balancerV2s)

  const balancerV3s = RELEVANT_BALANCER_V3_FORKS[chain] ?? []
  const balancerV3Calls = buildBalanceCalls(balancerV3s)

  const uniswapV4s = RELEVANT_UNISWAP_V4_FORKS[chain] ?? []
  const uniswapV4Calls = buildBalanceCalls(uniswapV4s)

  // Decimals resolution. The token list is the cheap source, but it is only
  // populated for the chains the caller happened to refresh — a chain missing
  // from that set used to fall through to a silent `?? 18`, which reports a
  // 6-decimal balance a trillion times too small (and, being plausible, is
  // caught by nothing downstream). So ask the chain for whatever the list
  // cannot answer.
  const decimalsByAsset: { [asset: string]: number } = {
    [zeroAddress]: NATIVE_DECIMALS,
  }
  for (const asset of unifiedAssets) {
    const fromList = list[asset]?.decimals
    if (typeof fromList === 'number') decimalsByAsset[asset] = fromList
  }

  const assetsMissingDecimals = unifiedAssets.filter(
    (asset) => decimalsByAsset[asset] === undefined,
  )
  const decimalsCalls: Call[] = assetsMissingDecimals.map((address) => ({
    name: 'decimals',
    address,
  }))

  const calls = [
    ...aaveCalls,
    ...balancerV2Calls,
    ...morphoCalls,
    ...balancerV3Calls,
    ...uniswapV4Calls,
  ]

  // appended last so the existing per-source offset walk below is untouched
  const decimalsOffset = calls.length
  calls.push(...decimalsCalls)

  const rawResults = await multicallRetry({
    chain,
    calls,
    abi: FlashAbi,
    batchSize: DEFAULT_BATCH_SIZE,
    maxRetries: 5,
    providerId: 0,
    allowFailure: true,
  })

  assetsMissingDecimals.forEach((asset, i) => {
    const parsed = parseDecimalsResult(rawResults[decimalsOffset + i])
    if (parsed !== undefined) decimalsByAsset[asset] = parsed
  })

  let liquidity: FlashLiquiditiesOnChain = {}

  let currentOffset = 0
  aaveProtocols.forEach((aave) => {
    const callLen = aaveAssets[aave].length * 2 + 1

    const data: any[] = rawResults.slice(currentOffset, callLen + currentOffset)
    const fee = data[callLen - 1]
    // update offset
    currentOffset += callLen

    // skip entire fork if premium call failed
    if (typeof fee !== 'bigint') return

    aaveAssets[aave].forEach((asset, i) => {
      const rawAmount = data[2 * i]
      const config = data[2 * i + 1]

      // skip failed calls
      if (typeof rawAmount !== 'bigint' || typeof config !== 'bigint') return

      const decimals = decimalsByAsset[asset]
      const enabled =
        !AAVE_V3_LENDERS.includes(aave as any) || getFlashLoanEnabled(config)
      if (
        enabled &&
        rawAmount > 0n &&
        FLASH_LOAN_IDS[aave as keyof typeof FLASH_LOAN_IDS] !== undefined
      ) {
        if (!liquidity[asset]) liquidity[asset] = []
        liquidity[asset].push({
          id: FLASH_LOAN_IDS[aave as keyof typeof FLASH_LOAN_IDS],
          name: aave,
          type: AAVE_V2_LENDERS.includes(aave as any)
            ? FlashLoanIds.AAVE_V2
            : FlashLoanIds.AAVE_V3,
          source: getAaveTypePoolAddress(chain, aave),
          fee: fee.toString(),
          availableRaw: rawAmount.toString(),
          available:
            decimals === undefined
              ? undefined
              : Number(formatUnits(rawAmount, decimals)),
          decimals,
        })
      }
    })
  })

  balancerV2s.forEach((balancer) => {
    const callLen = unifiedAssets.length

    const data: any[] = rawResults.slice(currentOffset, callLen + currentOffset)
    // update offset
    currentOffset += callLen

    unifiedAssets.forEach((asset, i) => {
      const decimals = decimalsByAsset[asset]
      const rawAmount = data[i]
      if (isValidResult(rawAmount) && rawAmount > 0n) {
        if (!liquidity[asset]) liquidity[asset] = []
        liquidity[asset].push({
          id: FLASH_LOAN_IDS[balancer.pool as keyof typeof FLASH_LOAN_IDS],
          name: balancer.pool,
          type: FlashLoanIds.BALANCER_V2,
          source: balancer.address,
          fee: '0',
          availableRaw: rawAmount.toString(),
          available:
            decimals === undefined
              ? undefined
              : Number(formatUnits(rawAmount, decimals)),
          decimals,
        })
      }
    })
  })

  morphoBs.forEach((morpho) => {
    const callLen = unifiedAssets.length

    const data: any[] = rawResults.slice(currentOffset, callLen + currentOffset)
    // update offset
    currentOffset += callLen

    unifiedAssets.forEach((asset, i) => {
      const rawAmount = data[i]
      if (isValidResult(rawAmount) && rawAmount > 0n) {
        if (!liquidity[asset]) liquidity[asset] = []
        const decimals = decimalsByAsset[asset]
        liquidity[asset].push({
          id: FLASH_LOAN_IDS[morpho.pool as keyof typeof FLASH_LOAN_IDS],
          name: morpho.pool,
          type: FlashLoanIds.MORPHO,
          source: morpho.address,
          fee: '0',
          availableRaw: rawAmount.toString(),
          available:
            decimals === undefined
              ? undefined
              : Number(formatUnits(rawAmount, decimals)),
          decimals,
        })
      }
    })
  })

  balancerV3s.forEach((balancerV3) => {
    const callLen = unifiedAssets.length

    const data: any[] = rawResults.slice(currentOffset, callLen + currentOffset)
    // update offset
    currentOffset += callLen

    unifiedAssets.forEach((asset, i) => {
      const decimals = decimalsByAsset[asset]
      const rawAmount = data[i]
      if (isValidResult(rawAmount) && rawAmount > 0n) {
        if (!liquidity[asset]) liquidity[asset] = []
        liquidity[asset].push({
          id: FLASH_LOAN_IDS[balancerV3.pool as keyof typeof FLASH_LOAN_IDS],
          name: balancerV3.pool,
          type: FlashLoanIds.SINGLETON,
          source: balancerV3.address,
          fee: '0',
          availableRaw: rawAmount.toString(),
          available:
            decimals === undefined
              ? undefined
              : Number(formatUnits(rawAmount, decimals)),
          decimals,
        })
      }
    })
  })

  uniswapV4s.forEach((uniV4) => {
    const callLen = unifiedAssets.length

    const data: any[] = rawResults.slice(currentOffset, callLen + currentOffset)
    // update offset
    currentOffset += callLen

    unifiedAssets.forEach((asset, i) => {
      const decimals = decimalsByAsset[asset]
      const rawAmount = data[i]
      if (isValidResult(rawAmount) && rawAmount > 0n) {
        if (!liquidity[asset]) liquidity[asset] = []
        liquidity[asset].push({
          id: FLASH_LOAN_IDS[uniV4.pool as keyof typeof FLASH_LOAN_IDS],
          name: uniV4.pool,
          type: FlashLoanIds.SINGLETON,
          source: uniV4.address,
          fee: '0',
          availableRaw: rawAmount.toString(),
          available:
            decimals === undefined
              ? undefined
              : Number(formatUnits(rawAmount, decimals)),
          decimals,
        })
      }
    })
  })

  return liquidity
}
